import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";

/**
 * One relationship card: a dropdown picking the top-level entity, and a
 * vertical Timeline below that re-derives and displays each linked record
 * live as the selection changes.
 *
 * `getSteps(selectedId)` returns an array of
 * `{ label, value, color?, empty? }` — `empty: true` renders the step
 * dimmed with "— none found —" instead of a value, for a hop that didn't
 * resolve to anything (e.g. no DeliveryDetails record for this instructor).
 */
const RelationshipFlowCard = ({
  title,
  selectLabel,
  options,
  optionsLoading,
  getSteps,
}) => {
  const [selectedId, setSelectedId] = useState("");

  const steps = selectedId ? getSteps(selectedId) : [];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{selectLabel}</InputLabel>
          <Select
            value={selectedId}
            label={selectLabel}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={optionsLoading}
          >
            <MenuItem value="">
              <em>{optionsLoading ? "Loading…" : `Select ${selectLabel}`}</em>
            </MenuItem>
            {(options || []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedId ? (
          <Timeline
            sx={{
              p: 0,
              m: 0,
              // Single-sided timeline: drop the default opposite-content
              // gutter since this page doesn't use TimelineOppositeContent.
              "& .MuiTimelineItem-root:before": {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {steps.map((step, index) => (
              <TimelineItem key={`${step.label}-${index}`}>
                <TimelineSeparator>
                  <TimelineDot
                    color={step.empty ? "grey" : step.color || "primary"}
                  />
                  {index < steps.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {step.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: step.empty ? 400 : 600,
                      fontStyle: step.empty ? "italic" : "normal",
                      color: step.empty ? "text.disabled" : "text.primary",
                    }}
                  >
                    {step.empty ? "— none found —" : step.value}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Pick a {selectLabel.toLowerCase()} above to see the flow.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RelationshipFlowCard;
